import {
  useEffect,
  useState,
} from "react";

import {
  state,
} from "../state/workspaceStore.js";

import {
  notFound,
} from "./NotFoundPage.jsx";

import {
  head,
} from "../components/common/PageHeader.jsx";

import {
  btn,
} from "../components/forms/FormControls.jsx";

import {
  nodes,
} from "../components/common/reactHelpers.jsx";

import {
  esc,
} from "../utils/format.js";

import {
  tableViews,
} from "../components/tables/RecordTable.jsx";


/* =========================================================
   API
========================================================= */

const COLLECTION_API =
  "https://api.praachinorganics.com/api/Collections";


/* =========================================================
   STATUS MAP
========================================================= */

function statusFromApi(
  status,
  statusName,
) {
  if (
    String(
      statusName || "",
    ).toLowerCase() ===
    "active"
  ) {
    return true;
  }

  if (
    String(
      statusName || "",
    ).toLowerCase() ===
    "inactive"
  ) {
    return false;
  }

  return Number(
    status,
  ) === 1;
}


/* =========================================================
   MAP COLLECTION DETAILS RESPONSE
========================================================= */

function mapCollectionDetails(
  item,
) {
  return {
    id:
      String(
        item?.id ??
        "",
      ),

    name:
      item?.name ||
      "",

    code:
      item?.code ||
      "",

    description:
      item?.description ||
      "",

    order:
      Number(
        item?.displayOrder ??
        1,
      ),

    launch:
      statusFromApi(
        item?.status,
        item?.statusName,
      ),

    archived:
      false,

    productCount:
      Number(
        item?.productCount ??
        0,
      ),

    orderCount:
      Number(
        item?.orderCount ??
        0,
      ),

    products:
      (
        Array.isArray(
          item?.products,
        )
          ? item.products
          : []
      ).map(
        (p) => ({
          id:
            String(
              p?.productId ??
              "",
            ),

          name:
            p?.productName ||
            "",

          collectionName:
            p?.collectionName ||
            "",

          isAssigned:
            Boolean(
              p?.isAssigned,
            ),
        }),
      ),
  };
}


/* =========================================================
   COLLECTION DETAIL UI
========================================================= */

export function collectionDetail(
  cid,
) {
  const c =
    state.db.collections.find(
      (c) =>
        String(
          c.id,
        ) ===
        String(
          cid,
        ),
    );

  if (!c) {
    return notFound();
  }

  const assignedProducts =
    Array.isArray(
      c.products,
    )
      ? c.products.filter(
        (p) =>
          p.isAssigned !== false,
      )
      : [];

  return (
    <>
      {head(
        "COLLECTIONS",

        c.name,

        c.description,

        btn(
          "Edit collection",
          `data-nav="collection-edit" data-id="${c.id}"`,
        ),
      )}

      <div
        className="grid-two"
      >
        <section
          className="card"
        >
          <h2>
            Collection details
          </h2>

          {nodes(
            [
              [
                "Name",
                c.name,
              ],

              [
                "Description",
                c.description,
              ],

              [
                "Product count",
                c.productCount ??
                assignedProducts.length,
              ],

              [
                "Code",
                c.code,
              ],

              [
                "Status",

                c.archived
                  ? "Archived"
                  : c.launch
                    ? "Active"
                    : "Inactive",
              ],

              [
                "Display order",
                c.order,
              ],
            ].map(
              (x) => (
                <div
                  key={
                    x[0]
                  }
                  className="detail-row"
                >
                  <span>
                    {x[0]}
                  </span>

                  <b>
                    {esc(
                      x[1],
                    )}
                  </b>
                </div>
              ),
            ),
          )}

          <div
            className="divider"
          ></div>

          {btn(
            "Archive collection",

            `data-confirm="archive-collection" data-id="${c.id}"`,

            true,
          )}

          <p
            className="hint"
          >
            {
              "A collection with product members cannot be archived. Reassign members first."
            }
          </p>
        </section>

        <section
          className="card"
        >
          <h2>
            Assigned products
          </h2>

          {
            assignedProducts.length > 0
              ? nodes(
                assignedProducts.map(
                  (p) => (
                    <button
                      key={
                        p.id
                      }
                      type="button"
                      className="attention-row"
                      data-nav="product"
                      data-id={
                        p.id
                      }
                    >
                      {esc(
                        p.name,
                      )}

                      <span>
                        {"→"}
                      </span>
                    </button>
                  ),
                ),
              )
              : (
                <p
                  className="empty"
                >
                  No products assigned.
                </p>
              )
          }
        </section>
      </div>
    </>
  );
}


/* =========================================================
   COLLECTION DETAILS PAGE
========================================================= */

export default function CollectionDetailsPage({
  recordId,
} = {}) {
  const [
    loading,
    setLoading,
  ] =
    useState(true);

  const [
    error,
    setError,
  ] =
    useState("");

  const [
    ,
    setRevision,
  ] =
    useState(0);


  /* =======================================================
     GET COLLECTION BY ID
     GET /api/Collections/{id}

     Products[] from this response is shown in
     Assigned products.
  ======================================================= */

  useEffect(() => {
    let alive =
      true;

    async function loadCollection() {
      try {
        setLoading(
          true,
        );

        setError(
          "",
        );

        const collectionId =
          Number(
            recordId,
          );

        if (
          !Number.isInteger(
            collectionId,
          ) ||
          collectionId <= 0
        ) {
          throw new Error(
            "A valid collection ID is required.",
          );
        }

        const response =
          await fetch(
            `${COLLECTION_API}/${collectionId}`,
            {
              method:
                "GET",

              headers: {
                Accept:
                  "application/json",
              },
            },
          );

        let data =
          null;

        try {
          data =
            await response.json();

        } catch {
          data =
            null;
        }

        if (
          !response.ok
        ) {
          throw new Error(
            data?.message ||
            data?.title ||
            `Unable to load collection (${response.status}).`,
          );
        }

        if (
          !alive
        ) {
          return;
        }

        const mappedCollection =
          mapCollectionDetails(
            data,
          );

        const index =
          state.db.collections.findIndex(
            (item) =>
              String(
                item.id,
              ) ===
              String(
                mappedCollection.id,
              ),
          );

        if (
          index >= 0
        ) {
          state.db.collections[
            index
          ] = {
            ...state.db.collections[
            index
            ],

            ...mappedCollection,
          };

        } else {
          state.db.collections.push(
            mappedCollection,
          );
        }

        setRevision(
          (n) =>
            n + 1,
        );

      } catch (error) {
        console.error(
          "COLLECTION DETAILS ERROR:",
          error,
        );

        if (
          alive
        ) {
          setError(
            error?.message ||
            "Unable to load the collection.",
          );
        }

      } finally {
        if (
          alive
        ) {
          setLoading(
            false,
          );
        }
      }
    }

    loadCollection();

    return () => {
      alive =
        false;
    };

  }, [
    recordId,
  ]);


  if (
    loading
  ) {
    return tableViews(
      <>
        {head(
          "COLLECTIONS",
          "Collection details",
          "Loading collection details...",
          <></>,
        )}

        <section
          className="card"
        >
          <p
            className="hint"
          >
            Loading collection...
          </p>
        </section>
      </>,
    );
  }


  if (
    error
  ) {
    return tableViews(
      <>
        {head(
          "COLLECTIONS",
          "Collection details",
          "",

          btn(
            "Back",
            'data-nav="collections"',
            true,
          ),
        )}

        <section
          className="card"
        >
          <p
            className="error"
            role="alert"
          >
            {error}
          </p>
        </section>
      </>,
    );
  }


  return tableViews(
    collectionDetail(
      recordId,
    ),
  );
}
